/**
 * Findings & Actions metinleri (K6; ADR 0094): operasyonel doğrulamanın bulguları, düzeltici
 * faaliyetler, yeniden doğrulama, DPA'ya yükseltme, RA değişiklik talebi ve kapanış. Adlar
 * ortağın belgelerindeki gibi İngilizce (FD §10; SS §7); Türkçe ikinci dil.
 */

export const riskFindingsEn = {
  title: 'Findings & Actions',
  description:
    'Findings raised from operational verifications: failed checks, new hazards and manual reports, their corrective actions, reverification, company escalation and closure.',
  shipNote:
    'A “No” answer is not automatically a non-conformity: it opens a finding to be corrected and reverified. A blocking finding stops the authorisation; an operation cannot be closed while a finding is open.',
  officeNote:
    'Findings are raised on board and arrive with synchronisation. In the office the DPA decides escalations and the library reviewer decides RA change requests.',
  vessel: 'Vessel',
  new: 'New Finding',
  stats: {
    open: 'Open',
    blocking: 'Blocking',
    overdue: 'Overdue',
    company: 'Awaiting Company',
  },
  search: 'Search by number, verification, operation, description or responsible',
  filter: {
    state: 'Status',
    states: {
      all: 'All',
      open: 'Open',
      blocking: 'Open and blocking',
      overdue: 'Overdue',
      company: 'Awaiting company decision',
      closed: 'Closed or cancelled',
    },
  },
  tableLabel: 'Findings',
  pagination: 'Findings pages',
  showing: '{{from}}–{{to}} of {{total}}',
  open: 'Open finding {{number}}',
  none: '—',
  columns: {
    number: 'Finding No',
    verification: 'Verification',
    type: 'Type',
    description: 'Description',
    responsible: 'Responsible / Due',
    actions: 'Actions',
    status: 'Status',
    company: 'Company',
  },
  actionsValue: '{{done}}/{{total}} done',
  blocking: 'Blocking',
  overdue: 'Overdue',
  types: {
    operational_constraint: 'Operational Constraint',
    equipment_defect: 'Equipment Defect',
    non_conformity: 'Non-Conformity',
    competency_gap: 'Competency Gap',
    new_hazard: 'New Hazard',
    other: 'Other',
  },
  sources: {
    failed_check: 'Failed check',
    manual: 'Manual report',
  },
  statuses: {
    open: 'Open',
    action_in_progress: 'Action in Progress',
    corrected: 'Corrected',
    reverified: 'Reverified',
    closed: 'Closed',
    cancelled: 'Cancelled',
  },
  requests: {
    escalation: {
      none: 'Not escalated',
      pending: 'Escalated to DPA',
      approved: 'DPA approved',
      returned: 'DPA returned',
    },
    change_request: {
      none: 'No change request',
      pending: 'RA change requested',
      accepted: 'RA change accepted',
      rejected: 'RA change rejected',
    },
  },
  empty: {
    title: 'No findings',
    ship: 'Findings are raised automatically when a verification is submitted with a “No”. You can also report one on a verification.',
    office: 'No findings on this vessel yet.',
    noVessel: 'You have no vessel to view.',
    noMatch: 'No finding matches the filter',
    error: 'Findings could not be loaded',
  },
  detail: {
    back: 'Back to findings',
    readOnly: 'Read only in the office',
    verification: 'Verification {{number}} · {{operation}}',
    openVerification: 'Open verification',
    findingCard: 'Finding',
    type: 'Type',
    source: 'Source',
    item: 'Check',
    round: 'Round {{round}}',
    description: 'Description',
    immediateAction: 'Immediate action',
    responsible: 'Responsible',
    dueOn: 'Due date',
    blocking: 'Work impact',
    blockingYes: 'Blocking: authorisation is held until closed',
    blockingNo: 'Not blocking',
    createdLabel: 'Raised',
    created: '{{name}}, {{time}}',
    actionsCard: 'Corrective Actions',
    noActions: 'No corrective action yet.',
    filesCard: 'Evidence Files',
    filesEmpty: 'No evidence file yet. Photos, test records and certificates are added on board.',
    filesAdd: 'Add evidence file',
    actionCols: {
      description: 'Action',
      responsible: 'Responsible',
      dueOn: 'Due',
      status: 'Status',
      evidence: 'Evidence',
    },
    completedOn: 'Completed {{day}}',
    cancelReason: 'Cancelled: {{reason}}',
    reverificationCard: 'Reverification',
    reverificationNone: 'The check has not been answered again in a later round.',
    reverificationValue: 'Round {{round}}: {{answer}}',
    reverificationManual: 'Not required for a manually reported finding.',
    answers: { yes: 'Yes', no: 'No', na: 'N/A' },
    linksCard: 'Linked Records',
    hse: 'HSE Non-Conformity',
    defect: 'PMS Defect',
    notLinked: 'Not linked',
    openRecord: 'Open {{number}}',
    companyCard: 'Company Review',
    noRequests: 'No escalation or change request.',
    decision: '{{decision}} by {{name}}{{role}}, {{time}}',
    history: 'History',
    closeBlockers: 'Before closing: {{list}}',
    closable: 'All conditions are met: the finding can be closed.',
    escalationPending: 'Escalated to the DPA: waiting for the company decision.',
    escalationReturned: 'The DPA returned the escalation: act on the comment and escalate again.',
  },
  blockers: {
    ACTIONS_OPEN: 'complete or cancel every action',
    CORRECTIVE_ACTION_REQUIRED: 'record a completed corrective action with evidence',
    ESCALATION_PENDING: 'wait for the DPA decision',
    ESCALATION_RETURNED: 'escalate again after the DPA comment',
    ESCALATION_REQUIRED: 'the new hazard needs the DPA’s approval',
    REVERIFICATION_REQUIRED: 'answer the check Yes in a reverification round',
  },
  events: {
    escalated: 'Escalated to DPA',
    change_requested: 'RA change requested',
    closed: 'Closed',
    reopened: 'Reopened',
    cancelled: 'Cancelled',
  },
  decisions: {
    approved: 'Approved',
    returned: 'Returned',
    accepted: 'Accepted',
    rejected: 'Rejected',
  },
  actions: {
    edit: 'Edit',
    escalate: 'Escalate to DPA',
    changeRequest: 'Request RA Change',
    close: 'Close',
    reopen: 'Reopen',
    cancel: 'Cancel Finding',
    openHse: 'Open HSE Non-Conformity',
    openDefect: 'Raise PMS Defect',
    decideEscalation: 'Decide Escalation',
    decideChangeRequest: 'Decide RA Change',
  },
  dialogs: {
    escalate: {
      title: 'Escalate {{number}} to the DPA',
      body: 'Describe the hazard and the controls you propose. The company reviews the safe controls; its approval does not replace correction and reverification.',
    },
    changeRequest: {
      title: 'Request a change to the risk assessment',
      body: 'Describe the change you propose to the company risk assessment. The library reviewer accepts or rejects it; an accepted change comes as a new revision.',
    },
    close: {
      title: 'Close {{number}}',
      body: 'Summarise the correction, the evidence and the reverification. The original “No” and the history are kept.',
    },
    reopen: {
      title: 'Reopen {{number}}',
      body: 'Give the reason for reopening the finding.',
    },
    cancel: {
      title: 'Cancel {{number}}',
      body: 'Only a finding reported in error, or one whose verification is cancelled, can be cancelled. Give the reason.',
    },
    openHse: {
      title: 'Open an HSE Non-Conformity',
      body: 'A draft Non-Conformity is created from this finding and linked to it. Complete and submit it in Compliance ▸ Non-Conformity. A “No” is not automatically a non-conformity: open one only when the SMS was not followed.',
      confirm: 'Open draft',
    },
    defect: {
      title: 'Raise a PMS defect',
      body: 'A defect (DM-17) is created from this finding and linked to it. Equipment can be linked later on the defect.',
      systemName: 'Equipment / System',
      severity: 'Severity',
      severities: { minor: 'Minor', major: 'Major', critical: 'Critical' },
      confirm: 'Raise defect',
    },
    decide: {
      escalationTitle: 'Decide the escalation of {{number}}',
      changeTitle: 'Decide the RA change request of {{number}}',
      request: 'Request',
      decision: 'Decision',
      comment: 'Comment',
      commentHint: 'Required when returning or rejecting.',
      confirm: 'Record decision',
    },
    form: {
      newTitle: 'New finding',
      editTitle: 'Edit {{number}}',
      verification: 'Verification',
      type: 'Type',
      description: 'Description',
      descriptionHint: 'What, where and when.',
      blocking: 'Blocking: hold the authorisation until the finding is closed',
      blockingFixed: 'A failed mandatory check always blocks the authorisation.',
      immediateAction: 'Immediate action',
      immediateActionHint: 'Stop or hold, isolate, correct, inform.',
      responsible: 'Responsible',
      dueOn: 'Due date',
      actions: 'Corrective actions',
      addAction: 'Add action',
      removeAction: 'Remove',
      action: {
        description: 'Action',
        responsible: 'Responsible',
        dueOn: 'Due',
        status: 'Status',
        completedOn: 'Completed on',
        evidence: 'Evidence',
        evidenceHint: 'Photo, document, test result or note.',
        cancelReason: 'Reason for cancelling',
      },
      actionStatuses: {
        open: 'Open',
        in_progress: 'In progress',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
      save: 'Save',
      create: 'Create finding',
      invalid: 'Complete the highlighted fields.',
    },
  },
  notice: {
    created: 'Finding {{number}} created.',
    saved: 'Finding saved.',
    event: '{{number}}: {{event}}.',
    hse: 'Draft Non-Conformity {{number}} opened and linked.',
    defect: 'Defect {{number}} raised and linked.',
    decided: 'Decision recorded.',
  },
  errors: {
    GENERIC: 'The action failed',
    INVALID_RA_FINDING: 'Check the finding fields: type and description are required.',
    ACTION_DESCRIPTION_REQUIRED: 'Every action needs a description.',
    EVIDENCE_REQUIRED: 'A completed action needs evidence.',
    COMPLETED_DATE_REQUIRED: 'A completed action needs the completion date.',
    CANCEL_REASON_REQUIRED: 'A cancelled action needs a reason.',
    FUTURE_DATE: 'The completion date cannot be in the future.',
    FINDING_LOCKED: 'The finding is closed or cancelled and can no longer change.',
    FINDING_EXISTS: 'This check already has a finding in this round.',
    FINDING_NOT_CANCELLABLE:
      'A failed check cannot be cancelled: correct it and reverify, or cancel the verification.',
    BLOCKING_REQUIRED: 'A failed mandatory check always blocks the authorisation.',
    VERIFICATION_CLOSED: 'The verification is closed or cancelled.',
    REQUEST_PENDING: 'A request of this kind is already waiting for the company.',
    ACTIONS_OPEN: 'Complete or cancel every action before closing.',
    CORRECTIVE_ACTION_REQUIRED:
      'Record a completed corrective action with evidence before closing.',
    ESCALATION_PENDING: 'The escalation is waiting for the DPA decision.',
    ESCALATION_RETURNED: 'The DPA returned the escalation: escalate again.',
    ESCALATION_REQUIRED: 'A new uncontrolled hazard needs the DPA’s approval before closing.',
    REVERIFICATION_REQUIRED: 'Answer the check Yes in a reverification round before closing.',
    LINK_FIXED: 'A record is already linked.',
    INVALID_LINK: 'The record cannot be linked.',
    DPA_REQUIRED: 'Only the DPA can decide an escalation.',
    ALREADY_DECIDED: 'This request has already been decided.',
    STALE_DECISION: 'A newer request exists. Reload and check again.',
    INVALID_TRANSITION: 'This action is not possible in the current status.',
    FORBIDDEN: 'You are not authorised for this action.',
    REASON_REQUIRED: 'Give a reason (at least 5 characters).',
    NOT_ASSIGNED: 'Your role on this vessel could not be confirmed.',
    VERSION_CONFLICT: 'The finding changed in the meantime. Reload and try again.',
  },
};

export const riskFindingsTr: typeof riskFindingsEn = {
  title: 'Bulgular ve Faaliyetler',
  description:
    'Operasyonel doğrulamalardan doğan bulgular: karşılanmayan maddeler, yeni tehlikeler ve elle bildirilenler; düzeltici faaliyetleri, yeniden doğrulama, şirkete yükseltme ve kapanış.',
  shipNote:
    '“Hayır” cevabı kendiliğinden uygunsuzluk değildir: düzeltilip yeniden doğrulanacak bir bulgu açar. Engelleyici bulgu yetkiyi durdurur; bulgu açıkken operasyon kapanmaz.',
  officeNote:
    'Bulgular gemide açılır ve senkronla gelir. Ofiste yükseltmeyi DPA, RA değişiklik talebini kütüphanenin gözden geçireni karara bağlar.',
  vessel: 'Gemi',
  new: 'Yeni Bulgu',
  stats: {
    open: 'Açık',
    blocking: 'Engelleyici',
    overdue: 'Geciken',
    company: 'Şirket Kararı Bekliyor',
  },
  search: 'Numara, doğrulama, operasyon, açıklama ya da sorumluyla arayın',
  filter: {
    state: 'Durum',
    states: {
      all: 'Hepsi',
      open: 'Açık',
      blocking: 'Açık ve engelleyici',
      overdue: 'Geciken',
      company: 'Şirket kararı bekliyor',
      closed: 'Kapanmış ya da iptal',
    },
  },
  tableLabel: 'Bulgular',
  pagination: 'Bulgu sayfaları',
  showing: '{{from}}–{{to}} / {{total}}',
  open: '{{number}} bulgusunu aç',
  none: '—',
  columns: {
    number: 'Bulgu No',
    verification: 'Doğrulama',
    type: 'Tür',
    description: 'Açıklama',
    responsible: 'Sorumlu / Son Tarih',
    actions: 'Faaliyetler',
    status: 'Durum',
    company: 'Şirket',
  },
  actionsValue: '{{done}}/{{total}} tamam',
  blocking: 'Engelleyici',
  overdue: 'Gecikmiş',
  types: {
    operational_constraint: 'Operasyonel Kısıt',
    equipment_defect: 'Ekipman Arızası',
    non_conformity: 'Uygunsuzluk',
    competency_gap: 'Yetkinlik Eksiği',
    new_hazard: 'Yeni Tehlike',
    other: 'Diğer',
  },
  sources: {
    failed_check: 'Karşılanmayan madde',
    manual: 'Elle bildirim',
  },
  statuses: {
    open: 'Açık',
    action_in_progress: 'Faaliyet Sürüyor',
    corrected: 'Düzeltildi',
    reverified: 'Yeniden Doğrulandı',
    closed: 'Kapandı',
    cancelled: 'İptal',
  },
  requests: {
    escalation: {
      none: 'Yükseltilmedi',
      pending: "DPA'ya yükseltildi",
      approved: 'DPA onayladı',
      returned: 'DPA geri gönderdi',
    },
    change_request: {
      none: 'Değişiklik talebi yok',
      pending: 'RA değişikliği istendi',
      accepted: 'RA değişikliği kabul edildi',
      rejected: 'RA değişikliği reddedildi',
    },
  },
  empty: {
    title: 'Bulgu yok',
    ship: 'Doğrulama “Hayır” ile gönderilince bulgu kendiliğinden açılır. Bir doğrulamada elle de bildirebilirsiniz.',
    office: 'Bu gemide henüz bulgu yok.',
    noVessel: 'Görebileceğiniz gemi yok.',
    noMatch: 'Süzgece uyan bulgu yok',
    error: 'Bulgular yüklenemedi',
  },
  detail: {
    back: 'Bulgulara dön',
    readOnly: 'Ofiste salt okunur',
    verification: 'Doğrulama {{number}} · {{operation}}',
    openVerification: 'Doğrulamayı aç',
    findingCard: 'Bulgu',
    type: 'Tür',
    source: 'Kaynak',
    item: 'Madde',
    round: '{{round}}. tur',
    description: 'Açıklama',
    immediateAction: 'Acil eylem',
    responsible: 'Sorumlu',
    dueOn: 'Son tarih',
    blocking: 'İşe etkisi',
    blockingYes: 'Engelleyici: kapanana dek yetki verilmez',
    blockingNo: 'Engellemez',
    createdLabel: 'Açılış',
    created: '{{name}}, {{time}}',
    actionsCard: 'Düzeltici Faaliyetler',
    noActions: 'Henüz düzeltici faaliyet yok.',
    filesCard: 'Kanıt Dosyaları',
    filesEmpty: 'Henüz kanıt dosyası yok. Fotoğraf, test kaydı ve belge gemide eklenir.',
    filesAdd: 'Kanıt dosyası ekle',
    actionCols: {
      description: 'Faaliyet',
      responsible: 'Sorumlu',
      dueOn: 'Son tarih',
      status: 'Durum',
      evidence: 'Kanıt',
    },
    completedOn: '{{day}} tamamlandı',
    cancelReason: 'İptal: {{reason}}',
    reverificationCard: 'Yeniden Doğrulama',
    reverificationNone: 'Madde sonraki bir turda yeniden cevaplanmadı.',
    reverificationValue: '{{round}}. tur: {{answer}}',
    reverificationManual: 'Elle bildirilen bulguda gerekmez.',
    answers: { yes: 'Evet', no: 'Hayır', na: 'N/A' },
    linksCard: 'Bağlı Kayıtlar',
    hse: 'HSE Uygunsuzluğu',
    defect: 'PMS Arızası',
    notLinked: 'Bağlı değil',
    openRecord: '{{number}} aç',
    companyCard: 'Şirket İncelemesi',
    noRequests: 'Yükseltme ya da değişiklik talebi yok.',
    decision: '{{decision}}: {{name}}{{role}}, {{time}}',
    history: 'Geçmiş',
    closeBlockers: 'Kapanıştan önce: {{list}}',
    closable: 'Bütün koşullar tamam: bulgu kapanabilir.',
    escalationPending: "DPA'ya yükseltildi: şirketin kararı bekleniyor.",
    escalationReturned: 'DPA yükseltmeyi geri gönderdi: nota göre davranıp yeniden yükseltin.',
  },
  blockers: {
    ACTIONS_OPEN: 'her faaliyeti tamamlayın ya da iptal edin',
    CORRECTIVE_ACTION_REQUIRED: 'kanıtlı tamamlanmış bir düzeltici faaliyet kaydedin',
    ESCALATION_PENDING: 'DPA kararını bekleyin',
    ESCALATION_RETURNED: 'DPA notundan sonra yeniden yükseltin',
    ESCALATION_REQUIRED: 'yeni tehlike DPA onayı ister',
    REVERIFICATION_REQUIRED: 'maddeyi bir yeniden doğrulama turunda Evet cevaplayın',
  },
  events: {
    escalated: "DPA'ya yükseltildi",
    change_requested: 'RA değişikliği istendi',
    closed: 'Kapandı',
    reopened: 'Yeniden açıldı',
    cancelled: 'İptal edildi',
  },
  decisions: {
    approved: 'Onaylandı',
    returned: 'Geri gönderildi',
    accepted: 'Kabul edildi',
    rejected: 'Reddedildi',
  },
  actions: {
    edit: 'Düzenle',
    escalate: "DPA'ya Yükselt",
    changeRequest: 'RA Değişikliği İste',
    close: 'Kapat',
    reopen: 'Yeniden Aç',
    cancel: 'Bulguyu İptal Et',
    openHse: 'HSE Uygunsuzluğu Aç',
    openDefect: 'PMS Arızası Aç',
    decideEscalation: 'Yükseltmeyi Karara Bağla',
    decideChangeRequest: 'RA Değişikliğini Karara Bağla',
  },
  dialogs: {
    escalate: {
      title: "{{number}} bulgusunu DPA'ya yükselt",
      body: 'Tehlikeyi ve önerdiğiniz kontrolleri yazın. Şirket güvenli kontrolleri inceler; onayı düzeltme ve yeniden doğrulamanın yerine geçmez.',
    },
    changeRequest: {
      title: 'Risk değerlendirmesinde değişiklik iste',
      body: 'Şirket risk değerlendirmesine önerdiğiniz değişikliği yazın. Kütüphanenin gözden geçireni kabul eder ya da reddeder; kabul edilen değişiklik yeni revizyonla gelir.',
    },
    close: {
      title: '{{number}} bulgusunu kapat',
      body: 'Düzeltmeyi, kanıtı ve yeniden doğrulamayı özetleyin. İlk “Hayır” ve geçmiş korunur.',
    },
    reopen: {
      title: '{{number}} bulgusunu yeniden aç',
      body: 'Bulguyu yeniden açma gerekçesini yazın.',
    },
    cancel: {
      title: '{{number}} bulgusunu iptal et',
      body: 'Yalnız yanlışlıkla bildirilen ya da doğrulaması iptal edilen bulgu iptal edilir. Gerekçeyi yazın.',
    },
    openHse: {
      title: 'HSE Uygunsuzluğu aç',
      body: 'Bu bulgudan taslak bir uygunsuzluk açılır ve bulguya bağlanır. Compliance ▸ Uygunsuzluk ekranında tamamlayıp gönderin. “Hayır” kendiliğinden uygunsuzluk değildir: yalnız SMS uygulanmadıysa açın.',
      confirm: 'Taslak aç',
    },
    defect: {
      title: 'PMS arızası aç',
      body: 'Bu bulgudan bir arıza (DM-17) açılır ve bulguya bağlanır. Ekipman arıza kaydında sonradan bağlanabilir.',
      systemName: 'Ekipman / Sistem',
      severity: 'Ciddiyet',
      severities: { minor: 'Küçük', major: 'Büyük', critical: 'Kritik' },
      confirm: 'Arıza aç',
    },
    decide: {
      escalationTitle: '{{number}} yükseltmesini karara bağla',
      changeTitle: '{{number}} RA değişiklik talebini karara bağla',
      request: 'Talep',
      decision: 'Karar',
      comment: 'Not',
      commentHint: 'Geri gönderme ve retle zorunlu.',
      confirm: 'Kararı kaydet',
    },
    form: {
      newTitle: 'Yeni bulgu',
      editTitle: '{{number}} düzenle',
      verification: 'Doğrulama',
      type: 'Tür',
      description: 'Açıklama',
      descriptionHint: 'Ne, nerede, ne zaman.',
      blocking: 'Engelleyici: bulgu kapanana dek yetki verilmesin',
      blockingFixed: 'Karşılanmayan zorunlu madde yetkiyi her zaman engeller.',
      immediateAction: 'Acil eylem',
      immediateActionHint: 'Durdur ya da beklet, ayır, düzelt, bildir.',
      responsible: 'Sorumlu',
      dueOn: 'Son tarih',
      actions: 'Düzeltici faaliyetler',
      addAction: 'Faaliyet ekle',
      removeAction: 'Kaldır',
      action: {
        description: 'Faaliyet',
        responsible: 'Sorumlu',
        dueOn: 'Son tarih',
        status: 'Durum',
        completedOn: 'Tamamlanma günü',
        evidence: 'Kanıt',
        evidenceHint: 'Fotoğraf, belge, test sonucu ya da not.',
        cancelReason: 'İptal gerekçesi',
      },
      actionStatuses: {
        open: 'Açık',
        in_progress: 'Sürüyor',
        completed: 'Tamamlandı',
        cancelled: 'İptal',
      },
      save: 'Kaydet',
      create: 'Bulgu aç',
      invalid: 'İşaretli alanları tamamlayın.',
    },
  },
  notice: {
    created: '{{number}} bulgusu açıldı.',
    saved: 'Bulgu kaydedildi.',
    event: '{{number}}: {{event}}.',
    hse: 'Taslak uygunsuzluk {{number}} açıldı ve bağlandı.',
    defect: '{{number}} arızası açıldı ve bağlandı.',
    decided: 'Karar kaydedildi.',
  },
  errors: {
    GENERIC: 'İşlem yapılamadı',
    INVALID_RA_FINDING: 'Bulgu alanlarını kontrol edin: tür ve açıklama zorunlu.',
    ACTION_DESCRIPTION_REQUIRED: 'Her faaliyetin açıklaması olmalı.',
    EVIDENCE_REQUIRED: 'Tamamlanan faaliyet kanıt ister.',
    COMPLETED_DATE_REQUIRED: 'Tamamlanan faaliyet tamamlanma günü ister.',
    CANCEL_REASON_REQUIRED: 'İptal edilen faaliyet gerekçe ister.',
    FUTURE_DATE: 'Tamamlanma günü gelecekte olamaz.',
    FINDING_LOCKED: 'Bulgu kapanmış ya da iptal edilmiş; artık değişmez.',
    FINDING_EXISTS: 'Bu maddenin bu turda zaten bulgusu var.',
    FINDING_NOT_CANCELLABLE:
      'Karşılanmayan madde iptal edilmez: düzeltip yeniden doğrulayın ya da doğrulamayı iptal edin.',
    BLOCKING_REQUIRED: 'Karşılanmayan zorunlu madde yetkiyi her zaman engeller.',
    VERIFICATION_CLOSED: 'Doğrulama kapanmış ya da iptal edilmiş.',
    REQUEST_PENDING: 'Bu türde bir talep zaten şirketin kararını bekliyor.',
    ACTIONS_OPEN: 'Kapatmadan önce her faaliyeti tamamlayın ya da iptal edin.',
    CORRECTIVE_ACTION_REQUIRED:
      'Kapatmadan önce kanıtlı tamamlanmış bir düzeltici faaliyet kaydedin.',
    ESCALATION_PENDING: 'Yükseltme DPA kararını bekliyor.',
    ESCALATION_RETURNED: 'DPA yükseltmeyi geri gönderdi: yeniden yükseltin.',
    ESCALATION_REQUIRED: 'Kontrolsüz yeni tehlike kapanıştan önce DPA onayı ister.',
    REVERIFICATION_REQUIRED:
      'Kapatmadan önce maddeyi bir yeniden doğrulama turunda Evet cevaplayın.',
    LINK_FIXED: 'Zaten bir kayıt bağlı.',
    INVALID_LINK: 'Kayıt bağlanamıyor.',
    DPA_REQUIRED: 'Yükseltmeyi yalnız DPA karara bağlar.',
    ALREADY_DECIDED: 'Bu talep zaten karara bağlandı.',
    STALE_DECISION: 'Daha yeni bir talep var. Yenileyip yeniden bakın.',
    INVALID_TRANSITION: 'Bu işlem mevcut durumda yapılamaz.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    REASON_REQUIRED: 'Gerekçe yazın (en az 5 karakter).',
    NOT_ASSIGNED: 'Bu gemideki rolünüz doğrulanamadı.',
    VERSION_CONFLICT: 'Bulgu bu arada değişti. Yenileyip yeniden deneyin.',
  },
};
